import { Clapperboard, Sparkles } from 'lucide-react';
import type { MediaAsset } from '../../../shared/types';
import { formatBytes } from '../../../shared/utils';
import { Badge } from '../ui';
import { cn } from '../../lib/utils';

export interface MediaGridProps {
  assets: MediaAsset[];
  onSelect?: (asset: MediaAsset) => void;
  selectedIds?: string[];
  className?: string;
}

/** Baseline grid. WEB-4 owns uploads, detail panel, alt text and renditions. */
export function MediaGrid({ assets, onSelect, selectedIds = [], className }: MediaGridProps) {
  return (
    <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', className)}>
      {assets.map((asset) => (
        <button
          key={asset.id}
          type="button"
          onClick={() => onSelect?.(asset)}
          className={cn(
            'group overflow-hidden rounded-lg border text-left transition-colors',
            selectedIds.includes(asset.id)
              ? 'border-accent ring-2 ring-accent/25'
              : 'border-border hover:border-border-strong',
          )}
        >
          <div className="relative aspect-square bg-sunken">
            {asset.kind === 'image' ? (
              <img
                src={asset.url}
                alt={asset.altText ?? asset.filename}
                loading="lazy"
                className="size-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-subtle">
                <Clapperboard className="size-7" aria-hidden />
              </div>
            )}
            {asset.source === 'ai_generated' && (
              <span className="absolute left-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
                <Sparkles className="size-3" aria-hidden />
              </span>
            )}
            {!asset.altText && (
              <span className="absolute right-1.5 top-1.5">
                <Badge tone="warning" size="sm">
                  No alt text
                </Badge>
              </span>
            )}
          </div>
          <div className="space-y-0.5 px-2.5 py-2">
            <p className="truncate text-xs font-medium text-fg">{asset.filename}</p>
            <p className="text-[11px] text-subtle">
              {formatBytes(asset.size)}
              {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}
